import { SubBranches } from '~/config'
import { Form } from '~/layers/forms/models'
import type {
  ConfigurationError,
  LocalConfigurations,
  RemoteConfigurations,
} from '~/server/types/configuration'

import type { AsyncDataRequestStatus } from '#app'

export type FormConfig = Record<
  number,
  {
    navigation?: boolean
    forms: Form[]
    argantoInsuranceStage?: SiteApiSchema<'OportunidadEtapa'>
    showQuotation?: boolean
  }
>

/**
 * Retrieves the form configuration for a given product.
 * @param {string | null} productCode The product code to retrieve the form configuration for.
 * @returns {Promise<FormConfig>} A promise that resolves to the form configuration for the given product.
 * @throws {ConfigurationError} If there is an error retrieving the form configuration.
 */
const getFormConfiguration = async (productCode: string | null): Promise<FormConfig> => {
  try {
    if (!productCode) return {}

    const { LocalConfigurationStore, RemoteConfigurationStore } = stores()

    const productInfo = LocalConfigurationStore.value?.productsInfo?.find(
      (p) => p.code === productCode
    )

    const branchInfo =
      LocalConfigurationStore.value?.argentoInsuranceConfig?.productSubBranches.find(
        (sb) => sb.id === productInfo?.argentoInsuranceSubBranchId
      )

    if (!productInfo) return {}

    const result = await $fetch<
      NuxtServerResponse<
        Nullable<{
          localconfiguration: LocalConfigurations
          remoteConfiguration: RemoteConfigurations
        }>,
        ConfigurationError
      >
    >('/api/form-data', {
      method: 'POST',
      body: {
        useComparator: productInfo.useComparator,
        argentoInsuranceProductId: productInfo.argentoInsuranceProductId,
        argentoInsuranceBranch: productInfo.argentoInsuranceBranch,
        argentoInsuranceSubBranch: branchInfo?.name,
        argentoInsuranceSubBranchId: branchInfo?.id,
      },
    })

    if (result.error) return {}

    let { formConfig: configuredForm } = result.data?.localconfiguration ?? {}

    if (!configuredForm) return {}

    // Build the form object as an array
    const formStepList = Object.values(configuredForm)

    //Check for user authentication
    const { user } = useUserSession()
    if (!user.value?.emailConfirmed) {
      const resumeStepIndex = formStepList.findIndex((step) => step.forms.includes(Form.resume))

      //Check swap resume step to include auth
      if (resumeStepIndex != -1) {
        const index = formStepList[resumeStepIndex]
        if (index) formStepList[resumeStepIndex + 1] = index
        formStepList[resumeStepIndex] = { forms: [Form.auth], argantoInsuranceStage: 'Otros' }
      } else {
        formStepList.push({ forms: [Form.auth] })
      }
    }

    //Rebuild the object
    configuredForm = formStepList.reduce((config: FormConfig, step, index) => {
      config[index] = step
      return config
    }, {})

    RemoteConfigurationStore.value = result.data?.remoteConfiguration
    LocalConfigurationStore.value = {
      ...LocalConfigurationStore.value,
      ...result.data?.localconfiguration,
      formConfig: configuredForm,
    }

    return configuredForm
  } catch {
    return {}
  }
}

/**
 * Builds the form configuration based on the remote configuration.
 *
 * The function uses the remote configuration to determine which forms to display in each step.
 * If the remote configuration is not available, an empty form configuration is returned.
 *
 * @returns {FormConfig} The form configuration based on the remote configuration.
 */
const buildForm = (): FormConfig => {
  const steps: FormConfig = {}
  const { RemoteConfigurationStore } = stores()
  if (!RemoteConfigurationStore.value) return steps

  if (RemoteConfigurationStore?.value?.initBy === 'ByProducto') {
    let coverageCriteriaForm: Form | undefined
    switch (RemoteConfigurationStore?.value?.ramoEnum) {
      case 'Viajes':
        coverageCriteriaForm = Form.travel
        break
    }

    const coverageStepForms = coverageCriteriaForm
      ? [coverageCriteriaForm, Form.coverage]
      : [Form.coverage]

    steps[0] = { forms: coverageStepForms, navigation: false }
  } else {
    steps[0] = { forms: [Form.coverage], navigation: false }
  }

  steps[1] = { forms: [Form.customer] }

  steps[4] = {
    forms: [Form.identification, Form.address],
  }

  switch (RemoteConfigurationStore?.value?.ramoEnum) {
    case 'Auto':
      steps[1].forms.push(Form.vehicle)
      break
    case 'Viajes':
      steps[1].forms.push(Form.vehicle)
      break
    case 'Personas':
      steps[4].forms.push(Form.beneficiaries)
      break
    case 'Hogar':
      switch (RemoteConfigurationStore?.value.subRamo?.nombre) {
        case SubBranches.garantiaDeElectrodomesticos: {
          steps[3] = { forms: [Form.device] }
          break
        }
        case SubBranches.incendioVoluntario:
          steps[3] = { forms: [Form.realState] }
          break
      }
      break
    case 'Mascotas':
      steps[3] = { forms: [Form.pet] }
      break
  }

  //Step 5 must be displayed only if user is not authenticated.
  const { user } = useUserSession()
  if (!user.value?.emailConfirmed) {
    steps[5] = { forms: [Form.auth] }
  }

  steps[6] = {
    forms: [Form.resume, Form.paymentInfo, Form.aditionalInfo, Form.tos],
  }

  return steps
}

/**
 * useFormBuilder is a composable function that retrieves the form configuration object
 * associated with a given product code and applies it to the stepper. It also provides
 * functions to navigate through the steps of the stepper and to build the form configuration
 * object from scratch.
 *
 * @param {string | null} productCode - The code of the product to retrieve the form configuration for.
 *
 * @returns {Object} - An object containing the following properties:
 *   - applyConfiguration: A function that applies the form configuration to the stepper.
 *   - status: A reactive state that tracks the status of the form configuration retrieval.
 *   - formConfig: A reactive state that contains the form configuration object.
 *   - activeStep: A reactive state that tracks the active step of the stepper.
 *   - continueToStep: A function that navigates through the steps of the stepper.
 *   - buildForm: A function that builds the form configuration object from scratch.
 */
export const useFormBuilder = (productCode: string | null) => {
  const formConfig = ref<FormConfig>({})
  const activeStep = ref<number>(0)
  const status = ref<AsyncDataRequestStatus>('idle')
  /**
   * Fetches and applies the form configuration.
   *
   * This function retrieves the form configuration object by making an asynchronous request
   * and assigns it to the `formConfig` reactive state. It also sets the `activeStep` to the minimum
   * step index found in the form configuration. If the form configuration is empty, the active step
   * remains at 0.
   */
  const applyConfiguration = async () => {
    status.value = 'pending'
    await getFormConfiguration(productCode)

    const { LocalConfigurationStore } = stores()

    formConfig.value = LocalConfigurationStore.value?.formConfig ?? {}

    const stepsIndex = Object.keys(formConfig.value)
    if (stepsIndex.length > 0) {
      activeStep.value = Math.min(...Object.keys(formConfig.value).map((step) => +step))
      status.value = 'success'
    } else {
      status.value = 'error'
    }
  }

  /**
   * Navigates through the steps of the stepper.
   * @param {string} param - Indicates whether to navigate to the next or previous step.
   * @param {'next'|'previous'} param.default - 'next' if not provided.
   */
  const continueToStep = (param: 'next' | 'previous' = 'next') => {
    const offset = param === 'previous' ? -1 : 1
    const stepsList = Object.keys(formConfig.value)
    const index = stepsList.indexOf(activeStep.value.toString())
    const step = stepsList[index + offset]
    if (step) activeStep.value = +step
  }

  const isLastStep = computed(() => {
    const stepsList = Object.keys(formConfig.value)
    const lasteStep = stepsList[stepsList.length - 1]
    return lasteStep === activeStep.value.toString()
  })

  return {
    applyConfiguration,
    status,
    formConfig,
    activeStep,
    continueToStep,
    isLastStep,
    buildForm,
  }
}
